import React, { useEffect, useState } from "react";
// Import Ionic
import { IonButton, IonIcon, IonInput, IonLabel } from "@ionic/react";

// Import Icons
import { basketSvg } from "@core/icons";
import { add, remove } from "ionicons/icons";

// Import Styles
import classNames from "classnames/bind";
import styles from "./ProductDetailCounter.module.scss";
import { Product as TProduct } from "@core/types";
import { RemoveFromCartHandler } from "../../../providers/cartDataStore";
import { useAuth } from "../../../contexts/auth-context";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../../providers/config";
import { UseProductDetailCounterReturn } from "../../../hooks/useProductDetailCounter";

interface IProductDetailCounter {
  isEmpty: boolean;
  isInStock: boolean;
  product: TProduct;
  selectedVariation: TProduct | null;
  cartQuantity: string | number;
  onAddToCart: (product: TProduct) => void;
  onRemoveFromCart: RemoveFromCartHandler;
  counterHook?: UseProductDetailCounterReturn;
}

const cx = classNames.bind(styles);

// For InnenrHtml Markup
const createMarkup = (product: any, selectedVariation: any, counterHook?: UseProductDetailCounterReturn) => {
  // If we have counterHook data, use the calculated price and show total
  if (counterHook) {
    const totalPrice = counterHook.getTotalPrice();
    const quantity = counterHook.getCurrentQuantity();

    return {
      __html: `<span><span>₽${totalPrice.toFixed(2)}</span><small style="display: block; font-size: 12px; opacity: 0.8;">${quantity} ${counterHook.selectedType === "unit" ? "шт" : "упак"}</small></span>`,
    };
  }

  // Fallback to original logic
  const _prod = product.type !== "variable" ? product : selectedVariation;
  if (_prod.price_html) {
    return { __html: _prod.price_html };
  }

  return { __html: `<span><span>₽${_prod.price}</span></span>` };
};

const ProductDetailCounter: React.FC<IProductDetailCounter> = ({
  isEmpty = false,
  isInStock = false,
  product,
  selectedVariation,
  cartQuantity,
  onAddToCart,
  onRemoveFromCart,
  counterHook,
}) => {
  const { t } = useTranslation();
  const { isUserAuthenticated } = useAuth();
  const config = useConfig();
  const maxQuantity =
    product.type === "variable" && selectedVariation ? selectedVariation.stockQuantity : product.stockQuantity;
  const [inputValue, setInputValue] = useState<number>(cartQuantity !== undefined ? Number(cartQuantity) : 0);

  const handleQuantityInputBlur = () => {
    let numericValue = Number(String(inputValue));
    const current = Number(cartQuantity);
    if (isNaN(numericValue)) numericValue = 1;
    if (numericValue === 0) {
      // Удаляем товар полностью из корзины
      for (let i = 0; i < current; i++) {
        onRemoveFromCart(selectedVariation?.id ?? product.id, isUserAuthenticated);
      }
      setInputValue(0);
      return;
    }
    if (numericValue < 1) numericValue = 1;
    if (numericValue > maxQuantity) numericValue = maxQuantity;
    setInputValue(numericValue);
    // Синхронизируем корзину только здесь
    const diff = numericValue - current;
    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        onAddToCart(product);
      }
    } else if (diff < 0) {
      for (let i = 0; i < Math.abs(diff); i++) {
        onRemoveFromCart(selectedVariation?.id ?? product.id, isUserAuthenticated);
      }
    }
  };

  const handleQuantityInputChange = (e: any) => {
    // IonInput возвращает e.detail.value (string | null)
    let value = e.detail.value ?? "";

    // Строгое ограничение на 4 знака
    if (value.length > 4) {
      // Обрезаем значение до 4 знаков
      value = value.slice(0, 4);
      e.target.value = value;
    }

    // Разрешаем только числа и пустую строку
    if (value === "") {
      setInputValue(0);
    } else {
      const numericValue = parseInt(value, 10);
      if (!isNaN(numericValue)) {
        setInputValue(numericValue);
      }
    }
  };

  // Sync input value with cartQuantity
  useEffect(() => {
    setInputValue(cartQuantity !== undefined ? Number(cartQuantity) : 0);
  }, [cartQuantity]);
  const { minLowStockThresholdToShowNumber, lowStockThresholdToShowPercent, showStockQuantity, showStockStatus } =
    useConfig();

  const remainingStock = product.stockQuantity - Number(cartQuantity);

  const isProductStockLow =
    showStockQuantity &&
    minLowStockThresholdToShowNumber &&
    product.stockQuantity &&
    (remainingStock <= minLowStockThresholdToShowNumber ||
      lowStockThresholdToShowPercent >= 100 - (Number(cartQuantity) / product.stockQuantity) * 100);

  const stockStatus =
    remainingStock <= 0
      ? "Количество ограничено"
      : isProductStockLow && `Доступно ${Math.floor(remainingStock)} ${product.portionUnit ?? t("PCE")}`;

  return (
    <div className={cx("counterBackdrop")}>
      <div className={cx("counter")}>
        {isEmpty ? (
          <div className={cx("counterWrapper", { disabled: !product?.purchasable })}>
            <div className={cx("counterPrice")}>
              <span
                className={cx("counterPriceSpan")}
                dangerouslySetInnerHTML={createMarkup(product, selectedVariation, counterHook)}
              ></span>
            </div>
            {isInStock && (
              <div
                className={cx("counterEmpty", counterHook && !counterHook.canAddToCart() && "disabled")}
                onClick={() => {
                  if (!counterHook || counterHook.canAddToCart()) {
                    onAddToCart(product);
                  }
                }}
              >
                <IonIcon src={basketSvg} className={cx("counterEmptyIcon")} />
              </div>
            )}
          </div>
        ) : (
          <div className={cx("counterWrapper")}>
            <div className={cx("counterPrice")}>
              <span
                className={cx("counterPriceSpan")}
                dangerouslySetInnerHTML={createMarkup(product, selectedVariation, counterHook)}
              ></span>
            </div>
            {showStockStatus && (
              <div className={"text-xs text-nowrap text-white absolute right-40"}>
                {product.stockStatus === "instock" && isProductStockLow && (
                  <IonLabel className={cx("stockStatus")}>{stockStatus}</IonLabel>
                )}
              </div>
            )}
            {isInStock && (
              <div className={cx("counterQuantity")}>
                <IonButton
                  fill={"clear"}
                  onClick={() => onRemoveFromCart(selectedVariation?.id ?? product.id, isUserAuthenticated)}
                  className={cx("ion-no-padding ion-no-margin", "counterQuantityButton")}
                >
                  <IonIcon src={remove} />
                </IonButton>
                <IonInput
                  min={1}
                  type="number"
                  maxlength={4}
                  pattern="[0-9]*"
                  max={maxQuantity}
                  value={inputValue}
                  inputMode="numeric"
                  onBlur={handleQuantityInputBlur}
                  onIonChange={handleQuantityInputChange}
                  onKeyDown={(e) => {
                    // Предотвращаем ввод больше 4 символов
                    const target = e.target as HTMLInputElement;
                    if (
                      target.value.length >= 4 &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight" &&
                      e.key !== "Tab"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                <IonButton
                  disabled={
                    !(
                      (product.type !== "variable" && product.stockQuantity >= Number(cartQuantity) + 1) ||
                      (product.type === "variable" &&
                        selectedVariation &&
                        selectedVariation.stockQuantity >= Number(cartQuantity) + 1)
                    )
                  }
                  fill={"clear"}
                  onClick={() => onAddToCart(product)}
                  className={cx("ion-no-padding  ion-no-margin", "counterQuantityButton")}
                >
                  <IonIcon src={add} />
                </IonButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailCounter;
